import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { zaikoApi } from '../../api/zaiko.js';
import { randomboxApi } from '../../api/randombox.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num, text } from '../../lib/format.js';

/**
 * 경품 실재고 현황.
 *
 * 원본: randombox/product/prize_zaiko_dashboard.php
 * 경품에 걸린 바코드로 실제 상품 재고를 찾아, 경품 수량과 나란히 본다.
 *
 * 원본의 '묶음상품 구성품' 은 옮기지 않았다 (아래 안내 참고).
 */

const CDN = 'https://cdn-banana.bizhost.kr/files/goods';

function defaultRange() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().slice(0, 10) };
}

export function ZaikoPage() {
  const [params, setParams] = useSearchParams();
  const fallback = defaultRange();
  const selected = params.get('id') ?? '';
  const from = params.get('from') ?? fallback.from;
  const to = params.get('to') ?? fallback.to;

  const boxes = useQuery({
    queryKey: ['randombox', 'product-options'],
    queryFn: () => randomboxApi.productOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const zaiko = useQuery({
    queryKey: ['zaiko', selected, from, to],
    queryFn: () => zaikoApi.load(selected, from, to),
    enabled: selected !== '',
  });

  const update = useUrlUpdate(params, setParams);

  const summary = useMemo(() => {
    const rows = zaiko.data?.rows ?? [];
    return {
      prizes: rows.length,
      soldout: rows.filter((r) => r.soldoutAt).length,
      /** 상품을 못 찾은 경품. 바코드가 틀렸거나 상품이 지워진 경우다. */
      unmatched: rows.filter((r) => !r.goodsIdx).length,
      /**
       * 경품 수량보다 실재고가 적은 건. 품절은 따로 세므로 여기서 뺀다 -
       * 표에도 품절 배지 하나만 뜨는데 요약에서 두 번 세면 숫자가 안 맞는다.
       */
      short: rows.filter(
        (r) => r.goodsIdx && !r.soldoutAt && (r.leftsL ?? 0) < r.quantity,
      ).length,
    };
  }, [zaiko.data]);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">N실재고 현황</h1>
          <p className="page-desc">
            경품에 걸린 바코드로 실제 상품 재고를 확인합니다.
            {zaiko.data?.name && ` — ${zaiko.data.name}`}
          </p>
        </div>
        <div className="page-head__actions">
          {zaiko.isFetching && <span className="page-head__busy">갱신 중…</span>}
        </div>
      </header>

      <div className="filters">
        <Field label="랜덤박스">
          <select className="input" value={selected} onChange={(e) => update({ id: e.target.value })}>
            <option value="">— 고르세요 —</option>
            {(boxes.data ?? [])
              .filter((o) => o.randomboxId !== null)
              .map((o) => (
                <option key={o.randomboxId} value={String(o.randomboxId)}>
                  {o.label} ({o.randomboxId})
                </option>
              ))}
          </select>
        </Field>
        <Field label="판매량 집계 시작">
          <input className="input" type="date" value={from} max={to} onChange={(e) => update({ from: e.target.value })} />
        </Field>
        <Field label="판매량 집계 끝">
          <input className="input" type="date" value={to} min={from} onChange={(e) => update({ to: e.target.value })} />
        </Field>
      </div>

      {selected === '' ? (
        <EmptyBox>랜덤박스를 고르면 보여줍니다.</EmptyBox>
      ) : zaiko.isPending ? (
        <Spinner />
      ) : zaiko.isError ? (
        <ErrorBox error={zaiko.error} onRetry={() => void zaiko.refetch()} />
      ) : zaiko.data.rows.length === 0 ? (
        <EmptyBox>등록된 경품이 없습니다.</EmptyBox>
      ) : (
        <>
          <div className="summary">
            <div className="summary__item">
              <span className="summary__label">경품</span>
              <strong className="summary__value">{num(summary.prizes)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">품절</span>
              <strong className="summary__value">{num(summary.soldout)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">재고 부족</span>
              <strong className="summary__value">{num(summary.short)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">상품 못 찾음</span>
              <strong className="summary__value">{num(summary.unmatched)}</strong>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>경품코드</th>
                  <th>등급</th>
                  <th>사진</th>
                  <th>상품</th>
                  <th>옵션</th>
                  <th>바코드</th>
                  <th style={{ textAlign: 'right' }}>경품수량</th>
                  <th style={{ textAlign: 'right' }}>재고</th>
                  <th style={{ textAlign: 'right' }}>가재고</th>
                  <th style={{ textAlign: 'right' }}>기간판매</th>
                  <th style={{ textAlign: 'center' }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {zaiko.data.rows.map((r) => {
                  const short = r.goodsIdx && (r.leftsL ?? 0) < r.quantity;
                  return (
                    <tr key={r.prizeCode}>
                      <td>{r.prizeCode}</td>
                      <td style={{ textAlign: 'center' }}>{text(r.grade)}</td>
                      <td>
                        {r.simg1 && r.goodsIdx ? (
                          <img
                            className="thumb"
                            src={`${CDN}/${encodeURIComponent(r.goodsIdx)}/${encodeURIComponent(r.simg1)}`}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <span className="td-muted">-</span>
                        )}
                      </td>
                      <td>{text(r.gname ?? r.productName)}</td>
                      <td className="td-muted">{text(r.opname)}</td>
                      <td className="td-muted">{text(r.barcode)}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.quantity)}</td>
                      <td style={{ textAlign: 'right' }} className={short ? 'cell-warn' : undefined}>
                        {r.leftsL === null ? <span className="td-muted">-</span> : num(r.leftsL)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {r.leftsF === null ? <span className="td-muted">-</span> : num(r.leftsF)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{num(r.sold)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {!r.goodsIdx ? (
                          <span className="badge badge--danger">상품없음</span>
                        ) : r.soldoutAt ? (
                          <span className="badge badge--danger">품절</span>
                        ) : short ? (
                          <span className="badge badge--warn">부족</span>
                        ) : (
                          <span className="td-muted">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="page-note">
            원본에 있던 <strong>묶음상품 구성품</strong> 표시는 옮기지 않았습니다. 원본이 그
            조회만 web 접속으로 하는데 지금 접속에는 <code>banana.shop_goods_opt</code> 읽기
            권한이 없고, 원본 주석에도 어느 컬럼이 부모인지 확인이 필요하다고 적혀 있었습니다.
          </p>
        </>
      )}
    </section>
  );
}
